import moment from 'moment';
import { CATEGORICAL_CHART_COLORS } from '../components/charts/chartColors';
import type { Theme } from '../theme';
import type {
  PaymentMethodBreakdown,
  PaymentMethodRow,
  PaymentMethodSeriesPoint,
} from '../redux/backoffice/dashboardSlice';

/**
 * Money-collected vocabulary used across the dashboard cards.
 *
 * The API calls the difference between what a customer hands over and what the
 * order costs `refunded`. In this POS that is not a refund — it is the change
 * handed back when the customer cannot pay to the exact shilling. So:
 *
 *   collected_gross = everything tendered at the till
 *   refunded        = change handed back
 *   collected_net   = what the business actually keeps  <-- matches sales
 *
 * Every card must therefore lead with `collected_net` and describe `refunded`
 * as change, never as a refund.
 */

/** Sort a copy of the method rows by net collected, biggest first. */
export const sortMethodsByNetDesc = (
  rows: PaymentMethodRow[] | undefined | null,
): PaymentMethodRow[] =>
  [...(rows || [])].sort((a, b) => Number(b?.net || 0) - Number(a?.net || 0));

/**
 * Map payment method names to stable colors. Branded methods keep their
 * recognised color; anything else walks the categorical palette.
 *
 * Build this once from the widest method list available (current period plus
 * any method that only appears in the previous period) and pass the result
 * down, so the summary card, the mix card and the chart never disagree.
 */
export const buildPaymentMethodColorMap = (
  methods: string[],
  theme: Theme,
): Record<string, string> => {
  const branded: Record<string, string> = {
    // M-Pesa owns emerald; cash takes brand blue. Amber stays reserved for
    // owed / warning UI and must not appear on payment methods.
    cash: theme.colors.primary,
    mpesa: theme.colors.successEmerald,
    'm-pesa': theme.colors.successEmerald,
    card: '#6366F1',
    'credit card': '#6366F1',
    'debit card': '#6366F1',
    bank: '#0EA5E9',
    'bank transfer': '#0EA5E9',
    airtel: '#DC2626',
    'airtel money': '#DC2626',
  };
  const map: Record<string, string> = {};
  let fallbackIdx = 0;
  methods.forEach(raw => {
    if (map[raw]) return;
    const key = String(raw || '')
      .trim()
      .toLowerCase();
    if (branded[key]) {
      map[raw] = branded[key];
    } else {
      map[raw] =
        CATEGORICAL_CHART_COLORS[fallbackIdx % CATEGORICAL_CHART_COLORS.length];
      fallbackIdx += 1;
    }
  });
  return map;
};

/** Percentage share formatted for display: whole numbers once past 10%. */
export const formatSharePercent = (share: number): string => {
  const pct = Math.max(0, Number(share) || 0) * 100;
  if (pct >= 10) return `${Math.round(pct)}%`;
  if (pct <= 0) return '0%';
  return `${Number(pct.toFixed(1))}%`;
};

/**
 * Title for the series chart. The backend coarsens the bucket for wide ranges,
 * so the label has to follow `bucket` rather than assume days.
 */
export const getSeriesChartTitle = (
  data: PaymentMethodBreakdown | null | undefined,
): string => {
  const bucket = String(data?.bucket || '').toLowerCase();
  if (bucket.startsWith('hour')) return 'Hourly payments by method';
  if (bucket.startsWith('day') || bucket.startsWith('daily'))
    return 'Daily payments by method';
  if (bucket.startsWith('week')) return 'Weekly payments by method';
  if (bucket.startsWith('month')) return 'Monthly payments by method';
  return 'Payments by method';
};

/** Inclusive number of days covered by the breakdown window. */
export const getPeriodDayCount = (
  data: PaymentMethodBreakdown | null | undefined,
): number => {
  const start = data?.startDate;
  const end = data?.endDate;
  if (!start || !end) return 1;
  const from = moment(start);
  const to = moment(end);
  if (!from.isValid() || !to.isValid()) return 1;
  const days = to.endOf('day').diff(from.startOf('day'), 'days') + 1;
  return days > 0 ? days : 1;
};

/** Total collected across a method list, used when totals are missing. */
const sumNet = (rows: PaymentMethodRow[] | undefined | null): number =>
  (rows || []).reduce((acc, row) => acc + Number(row?.net || 0), 0);

export type PaymentMixShiftRow = {
  method: string;
  netNow: number;
  netPrevious: number;
  netChange: number;
};

/** Why a mix comparison cannot be drawn, so the card can say so out loud. */
export type PaymentMixShiftUnavailable =
  | 'no_comparison'
  | 'no_previous_money'
  | 'no_current_money'
  | 'no_methods';

export type PaymentMixShift = {
  rows: PaymentMixShiftRow[];
  /** Non-null when there is nothing to chart. */
  unavailable: PaymentMixShiftUnavailable | null;
};

/**
 * How much more or less each method brought in vs the previous period.
 *
 * Always returns a result rather than null: a card the owner asked for should
 * explain why it is empty, not disappear.
 */
export const getPaymentMixShift = (
  data: PaymentMethodBreakdown | null | undefined,
): PaymentMixShift => {
  const empty = (unavailable: PaymentMixShiftUnavailable): PaymentMixShift => ({
    rows: [],
    unavailable,
  });

  const previous = data?.previousPeriod;
  if (!previous) return empty('no_comparison');

  const nowRows = data?.by_method || [];
  const previousRows = previous.by_method || [];
  const nowTotal = Number(data?.totals?.collected_net || 0) || sumNet(nowRows);
  const previousTotal =
    Number(previous?.totals?.collected_net || 0) || sumNet(previousRows);
  if (nowTotal <= 0) return empty('no_current_money');
  if (previousTotal <= 0) return empty('no_previous_money');

  const nowByMethod = new Map<string, number>();
  nowRows.forEach(row => {
    if (!row?.method) return;
    nowByMethod.set(row.method, Number(row.net || 0));
  });
  const previousByMethod = new Map<string, number>();
  previousRows.forEach(row => {
    if (!row?.method) return;
    previousByMethod.set(row.method, Number(row.net || 0));
  });

  const methods = Array.from(
    new Set([...nowByMethod.keys(), ...previousByMethod.keys()]),
  );

  const rows = methods
    .map(method => {
      const netNow = nowByMethod.get(method) || 0;
      const netPrevious = previousByMethod.get(method) || 0;
      return {
        method,
        netNow,
        netPrevious,
        netChange: netNow - netPrevious,
      };
    })
    .filter(row => row.netNow > 0 || row.netPrevious > 0)
    .sort((a, b) => b.netNow - a.netNow || b.netPrevious - a.netPrevious);

  if (!rows.length) return empty('no_methods');

  return { rows, unavailable: null };
};

export type CollectionPace = {
  /** Average value of a single payment. */
  averagePayment: number;
  /** Collections divided by days in the period. */
  perDay: number;
  /** Payment count divided by days in the period. */
  paymentsPerDay: number;
  dayCount: number;
  tenderCount: number;
  /** Busiest bucket in the series, when the series has enough shape to matter. */
  bestBucket: {
    label: string;
    amount: number;
    /** Share of the period's collections that landed in this bucket, 0..1. */
    share: number;
  } | null;
};

/**
 * Pace metrics derived purely by division. Returns null when no payment was
 * taken, because every figure would be zero.
 */
export const getCollectionPace = (
  data: PaymentMethodBreakdown | null | undefined,
): CollectionPace | null => {
  const tenderCount = Number(data?.totals?.tender_count || 0);
  const collectedNet = Number(data?.totals?.collected_net || 0);
  if (tenderCount <= 0) return null;

  const dayCount = getPeriodDayCount(data);
  const methods = (data?.by_method || []).map(row => row.method);
  const series: PaymentMethodSeriesPoint[] = data?.series || [];

  let bestBucket: CollectionPace['bestBucket'] = null;
  if (series.length && methods.length) {
    let nonZeroBuckets = 0;
    let bestLabel = '';
    let bestAmount = 0;
    for (const point of series) {
      const amount = methods.reduce(
        (acc, method) => acc + Number((point as any)?.[method] || 0),
        0,
      );
      if (amount > 0) nonZeroBuckets += 1;
      if (amount > bestAmount) {
        bestAmount = amount;
        bestLabel = String(point?.label || point?.bucket || '');
      }
    }
    // One busy bucket in an otherwise empty period says nothing.
    if (nonZeroBuckets >= 2 && bestAmount > 0 && bestLabel) {
      bestBucket = {
        label: bestLabel,
        amount: bestAmount,
        share: collectedNet > 0 ? bestAmount / collectedNet : 0,
      };
    }
  }

  return {
    averagePayment: collectedNet / tenderCount,
    perDay: collectedNet / dayCount,
    paymentsPerDay: tenderCount / dayCount,
    dayCount,
    tenderCount,
    bestBucket,
  };
};
